import * as Util from '@/ts/util/util';

export function interpolateBetweenRGB(colors: string[] | RGB[], amountBetween: number): RGB[] {
    const rgbColors = isRGBArray(colors) ? colors : colors.map(RGB.parseColor);
    const colorPairs: [RGB, RGB][] = [];
    for (let i = 0; i < (rgbColors.length - 1); i++) { // minus 1 intentional
        colorPairs.push([rgbColors[i], rgbColors[i + 1]]);
    }

    const interpolatedColors: RGB[] = [colorPairs[0][0]];
    for (const pair of colorPairs) {
        const first = pair[0];
        const second = pair[1];

        const amountOfAppendedColors = pair.length + amountBetween;
        const redInterpolation = Util.getRangeStepSpan([first.red, second.red], amountOfAppendedColors);
        const blueInterpolation = Util.getRangeStepSpan([first.blue, second.blue], amountOfAppendedColors);
        const greenInterpolation = Util.getRangeStepSpan([first.green, second.green], amountOfAppendedColors);

        for (let i = 1; i < amountOfAppendedColors; i++) { // offset by one to prevent same color getting appended twice
            interpolatedColors.push(new RGB(redInterpolation[i], greenInterpolation[i], blueInterpolation[i]));
        }
    }

    return interpolatedColors;
}

export function interpolateBetween(colors: string[] | RGB[], amountBetween: number): string[] {
    return interpolateBetweenRGB(colors, amountBetween).map(color => color.toString());
}

export function getColorFromSign(colors: [negative: string, zero: string, positive: string], value: number) {
    if (value > 0)
        return colors[2];
    if (value < 0)
        return colors[0];
    return colors[1];
}


export class RGB {
    private static MAX_COLOR_VALUE = 255;
    private redEightBit: number;
    private greenEightBit: number;
    private blueEightBit: number;

    constructor(red: number, green: number, blue: number) {
        const max = RGB.MAX_COLOR_VALUE;
        const roundedRed = Math.round(red);
        const roundedGreen = Math.round(green);
        const roundedBlue = Math.round(blue);

        if (roundedRed > max || roundedGreen > max || roundedBlue > max)
            throw new Error(`Individual value cannot exceed maximum value of ${max}. Values: ${red}, ${green}, ${blue}`);

        if (roundedRed < 0 || roundedGreen < 0 || roundedBlue < 0)
            throw new Error(`Individual value cannot be negative. Values: ${red}, ${green}, ${blue}`);

        this.redEightBit = roundedRed;
        this.greenEightBit = roundedGreen;
        this.blueEightBit = roundedBlue;
    }

    get isDark() {
        return (this.red * 0.299 + this.green * 0.587 + this.blue * 0.114) <= 186; // Hard coded based on: https://stackoverflow.com/questions/3942878/how-to-decide-font-color-in-white-or-black-depending-on-background-color
    }

    get red() {
        return this.redEightBit;
    }

    get blue() {
        return this.blueEightBit;
    }

    get green() {
        return this.greenEightBit;
    }

    get colorValues(): [red: number, green: number, blue: number] {
        return [this.redEightBit / RGB.MAX_COLOR_VALUE, this.greenEightBit / RGB.MAX_COLOR_VALUE, this.blueEightBit / RGB.MAX_COLOR_VALUE];
    }

    get hue(): number {
        const [red, green, blue] = this.colorValues;

        const maxValue = Math.max(red, green, blue);
        const minValue = Math.min(red, green, blue);

        if (maxValue === minValue)
            return 0;

        const maxMinDifference = maxValue - minValue;
        let hue = 0;
        switch (maxValue) {
            case red:
                hue = ((green - blue) % 6) / maxMinDifference;
                break;
            case green:
                hue = 2.0 + (blue - red) / maxMinDifference;
                break;
            case blue:
                hue = 4.0 + (red - green) / maxMinDifference;
                break;
        }

        hue *= 60;
        if (hue <= 0)
            hue += 360;

        return hue;
    }

    get saturation(): number {
        const [red, green, blue] = this.colorValues;

        const maxValue = Math.max(red, green, blue);
        const minValue = Math.min(red, green, blue);


        if (maxValue === minValue)
            return 0;

        return this.luminosity < 0.5 ? ((maxValue - minValue) / (maxValue + minValue)) : ((maxValue - minValue) / (2 - maxValue - minValue));
    }

    get luminosity(): number {
        const [red, green, blue] = this.colorValues;

        const maxValue = Math.max(red, green, blue);
        const minValue = Math.min(red, green, blue);

        return Util.getAverage([maxValue, minValue]);
    }

    static parseColor(rgb: string): RGB;
    static parseColor(rgb: string, defaultColor: RGB): RGB;
    static parseColor(rgb: string, defaultColor?: RGB): RGB {
        try {
            if (isValidHexNotation(rgb))
                return RGB.parseColorHex(rgb);
            if (isValidRgbNotation(rgb))
                return RGB.parseCssRgb(rgb);
        } catch(err) {
            if (!defaultColor)
                throw err;
        }

        if (defaultColor)
            return defaultColor;
        throw new Error(`Invalid color entered: ${rgb}`);
    }

    private static parseColorHex(hex: string): RGB {
        const strippedHex = Util.removeAllFromString(hex, '0x', '#');

        if (strippedHex.length !== 6)
            throw new Error('Invalid hex value: Must have 6 value places');

        const red = Util.parseHex(strippedHex.slice(0, 2));
        const green = Util.parseHex(strippedHex.slice(2, 4));
        const blue = Util.parseHex(strippedHex.slice(4, 6));

        return new RGB(red, green, blue);
    }

    private static parseCssRgb(rgb: string): RGB {
        const stripped = Util.removeAllFromString(rgb.toLowerCase(), 'rgb', '(', ')', ' ');
        const rgbValues: number[] = stripped.split(',').map(value => Number(value));

        if (rgbValues.length !== 3)
            throw new Error('Invalid CSS rgb color');

        return new RGB(rgbValues[0], rgbValues[1], rgbValues[2]);
    }

    private static getHexComponent(value: number) {
        const hexString = value.toString(16).toUpperCase();
        return hexString.length === 1 ? '0' + hexString : hexString;
    }

    equals(other: RGB): boolean {
        return this.toString() === other.toString();
    }

    toString(): string {
        const red = RGB.getHexComponent(this.redEightBit);
        const green = RGB.getHexComponent(this.greenEightBit);
        const blue = RGB.getHexComponent(this.blueEightBit);
        return `#${red}${green}${blue}`;
    }
}


function isValidHexNotation(color: string): color is `#${number}` | `0x${number}` {
    const trimmedColor = color.trim();
    const hasHexNotation = trimmedColor.startsWith('#') || trimmedColor.startsWith('0x');
    const colorValues = Util.removeAllFromString(trimmedColor, '#', '0x');
    const hasValidLength = (colorValues.length === 3 || colorValues.length === 6);
    const areAllCharactersValid = colorValues.split('').every(char => !isNaN(parseInt(char, 16)));

    return hasHexNotation && hasValidLength && areAllCharactersValid;
}

function isValidRgbNotation(color: string): color is `rgb(${number} ${number} ${number})` | `rgb(${number}, ${number}, ${number})` {
    const trimmedColor = color.trim();
    const isWrapped = trimmedColor.startsWith('rgb(') && trimmedColor.endsWith(')');

    const strippedNumbers = Util.removeAllFromString(color.toLowerCase(), 'rgb', '(', ')');
    const numberStrings = strippedNumbers.includes(',') ? strippedNumbers.split(',') : strippedNumbers.split(' ');
    const numbers = numberStrings.map(string => Number(string.trim()));
    const areValidNumbers = numbers.every(number => !isNaN(number) && number <= 255 && number >= 0);

    return isWrapped && numbers.length === 3 && areValidNumbers;
}

export function isRGBArray(array: unknown[]): array is RGB[] {
    if (array.length === 0)
        return true;
    for (const element of array) {
        if (!(element instanceof RGB))
            return false;
    }
    return true;
}